import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Loader2, RotateCcw, Save, Cpu } from "lucide-react";
import { toast } from "sonner";
import { UniverseManager } from "@/components/admin/UniverseManager";

const ENGINE_LABELS = {
  ensemble: "Ensemble · 3 models (max accuracy, 3× cost)",
  solo: "Single model · Claude-first ladder",
  flash: "Cheapest · Gemini-Flash-first ladder",
};

const Pill = ({ active, isDefault, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
          className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest border transition-colors ${
            active ? "border-[#007AFF] text-[#3B9BFF] bg-[#007AFF]/10" : "border-zinc-800 text-zinc-500 hover:text-white"}`}>
    {children}
    {isDefault && <span className="ml-1.5 text-[8px] px-1 py-0.5 bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40">DEFAULT</span>}
  </button>
);

/** Admin AI Cost Controls — every LLM-spend rule is tunable here, nothing hardwired. */
export const AICostControls = () => {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/ai-settings");
      setCfg(data.settings);
      setDefaults(data.defaults);
    } catch (e) {
      console.error("ai-settings load failed:", e?.message || e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/ai-settings", next || cfg);
      setCfg(data.settings);
      toast.success("AI cost controls saved — applies within ~30s");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Could not save settings");
    } finally { setBusy(false); }
  };

  if (!cfg || !defaults) return null;
  const isDefaultState = Object.keys(defaults).every((k) => JSON.stringify(cfg[k]) === JSON.stringify(defaults[k]));

  return (
    <div className="mt-3 bg-[#121212] border border-zinc-800 p-3" data-testid="ai-cost-controls">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Cpu className="h-3.5 w-3.5 text-[#007AFF]" />
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">AI Cost Controls</span>
          {isDefaultState && (
            <span className="text-[8px] px-1.5 py-0.5 bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40 font-mono uppercase tracking-widest">Default settings active</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => save(defaults)} disabled={busy}
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2 py-1 border border-zinc-800 text-zinc-400 hover:text-white font-mono disabled:opacity-50"
                  data-testid="ai-cc-reset">
            <RotateCcw className="h-3 w-3" /> Reset to defaults
          </button>
          <button onClick={() => save()} disabled={busy}
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest px-2.5 py-1 border border-[#007AFF]/60 text-[#3B9BFF] hover:bg-[#007AFF]/10 font-mono font-bold disabled:opacity-50"
                  data-testid="ai-cc-save">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* User signal engine */}
        <div>
          <div className="text-[10px] text-zinc-300 font-medium mb-0.5">User signals engine</div>
          <div className="text-[9px] text-zinc-600 font-mono mb-1.5">When a client clicks a symbol</div>
          <div className="flex flex-wrap gap-1">
            {["solo", "ensemble"].map((e) => (
              <Pill key={e} active={cfg.user_engine === e} isDefault={defaults.user_engine === e}
                    onClick={() => setCfg({ ...cfg, user_engine: e })} testid={`ai-cc-user-${e}`}>
                {e === "solo" ? "Single model" : "Ensemble 3×"}
              </Pill>
            ))}
          </div>
          <div className="text-[9px] text-zinc-600 mt-1 leading-snug">{ENGINE_LABELS[cfg.user_engine]}</div>
        </div>

        {/* Scanner engine */}
        <div>
          <div className="text-[10px] text-zinc-300 font-medium mb-0.5">Background scanner engine</div>
          <div className="text-[9px] text-zinc-600 font-mono mb-1.5">Auto-scans + Top Setups</div>
          <div className="flex flex-wrap gap-1">
            {["flash", "solo", "ensemble"].map((e) => (
              <Pill key={e} active={cfg.scanner_engine === e} isDefault={defaults.scanner_engine === e}
                    onClick={() => setCfg({ ...cfg, scanner_engine: e })} testid={`ai-cc-scanner-${e}`}>
                {e === "flash" ? "Flash (cheapest)" : e === "solo" ? "Single model" : "Ensemble 3×"}
              </Pill>
            ))}
          </div>
          <div className="text-[9px] text-zinc-600 mt-1 leading-snug">{ENGINE_LABELS[cfg.scanner_engine]}</div>
        </div>

        {/* Market hours gate */}
        <div>
          <div className="text-[10px] text-zinc-300 font-medium mb-0.5">Scanner market-hours gate</div>
          <div className="text-[9px] text-zinc-600 font-mono mb-1.5">NSE Mon–Fri 09:15–15:30 IST</div>
          <div className="flex gap-1">
            <Pill active={cfg.market_hours_only} isDefault={defaults.market_hours_only === true}
                  onClick={() => setCfg({ ...cfg, market_hours_only: true })} testid="ai-cc-hours-on">
              Market hours only
            </Pill>
            <Pill active={!cfg.market_hours_only} isDefault={defaults.market_hours_only === false}
                  onClick={() => setCfg({ ...cfg, market_hours_only: false })} testid="ai-cc-hours-off">
              24/7
            </Pill>
          </div>
          <div className="text-[9px] text-zinc-600 mt-1 leading-snug">
            {cfg.market_hours_only ? "Zero scanner spend nights, weekends & holidays." : "Scanner runs round the clock — highest cost."}
          </div>
        </div>

        {/* Numeric knobs */}
        <div className="space-y-2">
          <div>
            <div className="text-[10px] text-zinc-300 font-medium">Signal cache freshness (day mode)</div>
            <div className="flex items-center gap-2 mt-1">
              <input type="number" min={1} max={120} value={cfg.day_cache_ttl_min}
                     onChange={(e) => setCfg({ ...cfg, day_cache_ttl_min: Math.max(1, Math.min(120, Number(e.target.value) || 1)) })}
                     className="w-16 bg-transparent border border-zinc-800 focus:border-[#007AFF] outline-none px-2 py-1 text-xs font-mono"
                     data-testid="ai-cc-ttl" />
              <span className="text-[9px] text-zinc-600 font-mono">min · default {defaults.day_cache_ttl_min}</span>
            </div>
          </div>
          <div>
            <div className="text-[10px] text-zinc-300 font-medium">Max AI calls / user / hour</div>
            <div className="flex items-center gap-2 mt-1">
              <input type="number" min={0} max={1000} value={cfg.user_hourly_cap}
                     onChange={(e) => setCfg({ ...cfg, user_hourly_cap: Math.max(0, Math.min(1000, Number(e.target.value) || 0)) })}
                     className="w-16 bg-transparent border border-zinc-800 focus:border-[#007AFF] outline-none px-2 py-1 text-xs font-mono"
                     data-testid="ai-cc-cap" />
              <span className="text-[9px] text-zinc-600 font-mono">0 = unlimited · default {defaults.user_hourly_cap}</span>
            </div>
          </div>
        </div>
      </div>

      <UniverseManager />
    </div>
  );
};
